import { useState, useEffect, useCallback, useRef } from "react";
import { createVerificationSession, getLegalIdVerificationStatus, patchLegalIdVerification, ApiError } from "../../shared/api";
import {
  addAPIBreadcrumb,
  addVerificationFlowBreadcrumb,
} from "../../shared/utils/sentryBreadcrumbs";
import {
  setVerificationUser,
  setVerificationAttemptId,
  trackFlowError,
} from "../../shared/utils/sentryTracking";
import { getAllowedDomains } from "../../shared/utils/apiConfig";
import { resolveParentOrigin } from "../../shared/utils/parentOrigin";
import { useRenderedSignal } from "./useRenderedSignal";
import { isTokenExpiringSoon } from "../../shared/utils/tokenUtils";
import { logger } from "../../shared/utils/logger";
import { setAnalyticsIdentity, setAnalyticsEnabled, resolveReferenceType, type ParentService } from "../../shared/analytics/analyticsClient";
import { trackViewed, trackOutcomePassed, trackOutcomeFailed } from "../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS, type OutcomeSection } from "../../shared/analytics/documentCaptureEvents";
import type {
  DocumentSuccessPayload,
  DocumentErrorPayload,
  DocumentFailedApi,
  VerificationCompleteMessage,
  VerificationErrorMessage,
  VerificationReloadMessage,
} from "../../shared/types";
import type { LegalIdVerificationResponse } from "../../shared/types/verification";

export type LegalIdVerificationState = "initializing" | "ready" | "capturing" | "processing" | "confirming" | "completed" | "error";

/** Maps HTTP status code to the appropriate DocumentErrorCode */
function errorCodeFromStatus(statusCode: number | undefined): DocumentErrorPayload["errorCode"] {
  if (statusCode === 401) return "session_invalid";
  if (statusCode === 400) return "session_invalid";
  if (statusCode !== undefined && statusCode >= 500) return "internal_error";
  return "network_error";
}

/**
 * Which section a failure at each stage belongs to. `null` means "emit nothing":
 * "ready"'s only failure (user_no_id) is already a Clicked, and "completed"/"error"
 * are terminal — that pair is what stops a second failure emitting a second Outcome.
 */
const OUTCOME_SECTION_BY_STAGE: Record<LegalIdVerificationState, OutcomeSection | null> = {
  initializing: DOCUMENT_CAPTURE_SECTIONS.INIT,
  ready: null,
  capturing: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE,
  processing: DOCUMENT_CAPTURE_SECTIONS.PROCESSING,
  confirming: DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION,
  completed: null,
  error: null,
};

/**
 * The attempt's single terminal failure Outcome, routed by the stage it failed in.
 * Unlike SSN Card, Legal ID needs no error-code special-casing: every DocumentErrorCode
 * is already IDMe's own vocabulary (no vendor-token remapping), and there is no
 * "rejection still counts as passed" case here — `passed` fires from exactly one place,
 * confirmVerification()'s PATCH-success branch.
 */
function trackTerminalFailure(stage: LegalIdVerificationState, errorCode: DocumentErrorPayload["errorCode"]): void {
  const section = OUTCOME_SECTION_BY_STAGE[stage];
  if (!section) return;
  trackOutcomeFailed(section, errorCode);
}

/**
 * Custom hook to manage legal ID verification flow
 *
 * Handles:
 * - Receiving initialization data via postMessage from parent window
 * - Creating verification session with backend (POST /v1/verifications/legal-ids)
 * - Providing Veriff session URL for the useVeriffEvents hook
 * - Polling for verification status (GET /v1/verifications/legal-ids/{id})
 * - Sending results to parent window via postMessage
 */
export function useLegalIdVerification() {
  const [state, setState] = useState<LegalIdVerificationState>("initializing");
  const [error, setError] = useState<string | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [veriffSessionUrl, setVeriffSessionUrl] = useState<string | null>(null);
  const [parentOrigin, setParentOrigin] = useState<string | null>(null);
  const [verificationResult, setVerificationResult] = useState<{ data: LegalIdVerificationResponse } | null>(null);
  const [fallbackAvailable, setFallbackAvailable] = useState(false);
  // Stored as a ref — expiry is checked imperatively before API calls, not used for rendering
  const tokenExpiresAtRef = useRef<number | null>(null);

  useRenderedSignal(state !== "initializing", parentOrigin);

  // Latest state, readable synchronously from callbacks (e.g. to know which stage
  // a failure happened in for analytics) without stale-closure issues.
  const stateRef = useRef<LegalIdVerificationState>("initializing");

  // Updates stateRef synchronously (not via an effect) so async continuations — e.g.
  // pollVerificationStatus after an `await` — never read a stale stage for analytics
  // routing. Every transition goes through this; aliased onto `setState` below.
  const setFlowState = useCallback((next: LegalIdVerificationState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  // Set user context in Sentry when verification ID is available
  useEffect(() => {
    if (verificationId) {
      setVerificationUser(verificationId, sessionToken?.substring(0, 8));
    }
  }, [verificationId, sessionToken]);

  // Track state transitions as breadcrumbs
  useEffect(() => {
    addVerificationFlowBreadcrumb(state, verificationId);
  }, [state, verificationId]);

  const initializeLegalIdVerification = async () => {
    try {
      // Resolve the parent origin robustly. A plain document.referrer breaks after
      // a Veriff onReload window.location.reload(): the reloaded iframe's referrer
      // becomes its OWN origin, so IDME_READY would target ourselves, the parent
      // never replies with IDME_INIT, and the flow hangs. resolveParentOrigin()
      // prefers ancestorOrigins / a persisted value and never returns self.
      const origin = resolveParentOrigin();
      if (!origin) {
        throw new Error("Cannot determine a valid parent origin (referrer/ancestorOrigins/stored all unavailable or not allowlisted)");
      }

      logger.log("[LegalIdVerification] Parent origin validated:", origin);
      setParentOrigin(origin);

      const handleInitMessage = async (event: MessageEvent) => {
        // Only accept the token-bearing INIT from the validated parent origin. `origin` was
        // already resolved via resolveParentOrigin() above, so pinning event.origin to it
        // rejects IDME_INIT messages forged by any other (e.g. malicious) frame.
        if (event.origin !== origin) {
          logger.warn("[LegalIdVerification] Ignoring IDME_INIT from unexpected origin:", event.origin);
          return;
        }
        if (!event.data || event.data.type !== "IDME_INIT") {
          return;
        }

        logger.log("[LegalIdVerification] Received initialization data via postMessage");

        const payload = event.data.payload;
        const {
          sessionToken: token,
          tokenExpiresAt,
          externalReferenceId,
          parentService,
          analyticsEnabled,
          fallbackAvailable: initFallbackAvailable,
        } = payload ?? {};

        // Bind identity + the host's rollout gate, and emit the session-started
        // milestone, as early as possible — unconditionally, before the payload-shape
        // check and the token-expiry guard below. Both of those still emit their own
        // Outcome under this same elementName, so without this they'd ship with no
        // referenceId, the gate still off, and no paired Viewed to explain them.
        const resolvedReferenceId = externalReferenceId || `web_${Date.now()}`;
        const resolvedParentService: ParentService = (parentService as ParentService | undefined) ?? "iva";
        setAnalyticsIdentity({
          parentService: resolvedParentService,
          referenceType: resolveReferenceType(resolvedParentService),
          referenceId: resolvedReferenceId,
        });
        setAnalyticsEnabled(analyticsEnabled === true);
        trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.SESSION_STARTED, "section", { section: DOCUMENT_CAPTURE_SECTIONS.INIT });

        if (!payload) {
          logger.error("[LegalIdVerification] IDME_INIT received without payload");
          const allowedOrigin = getAllowedDomains().some(domain => {
            const hostname = new URL(origin).hostname;
            return hostname === domain || hostname.endsWith(`.${domain}`);
          });
          if (allowedOrigin) {
            window.parent.postMessage({
              source: "idme-verification",
              type: "VERIFICATION_ERROR",
              payload: { errorCode: "session_invalid", message: "Invalid initialization payload" },
            } as VerificationErrorMessage, origin);
          }
          trackTerminalFailure("initializing", "session_invalid");
          setFlowState("error");
          setError("Invalid initialization payload");
          return;
        }

        if (typeof tokenExpiresAt === "number") {
          tokenExpiresAtRef.current = tokenExpiresAt;
        }
        setFallbackAvailable(initFallbackAvailable === true);

        // Tag the session with the IVA attempt id (the SDK's externalReferenceId)
        // as early as possible — before session creation — so even a session-
        // creation failure is joinable to the IVA attempt, and so it's present on
        // the reload_request/canceled events. Stable across camera-permission
        // onReload remints, unlike verificationId.
        if (externalReferenceId) {
          setVerificationAttemptId(externalReferenceId);
        }

        window.removeEventListener("message", handleInitMessage);

        try {
          // Guard: abort if the token won't last another 2 minutes
          if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
            logger.warn("[LegalIdVerification] Token expiring soon, aborting session creation");
            trackTerminalFailure("initializing", "token_expired");
            setFlowState("error");
            setError("Session token has expired");
            const allowedOrigin = getAllowedDomains().some(domain => {
              const hostname = new URL(origin).hostname;
              return hostname === domain || hostname.endsWith(`.${domain}`);
            });
            if (allowedOrigin) {
              window.parent.postMessage({
                source: "idme-verification",
                type: "VERIFICATION_ERROR",
                payload: { errorCode: "token_expired", message: "Session token has expired", failedApi: "post_legal_id_failed" as const },
              } as VerificationErrorMessage, origin);
            }
            return;
            // sessionToken is never set — no dependent effects trigger
          }

          setSessionToken(token);
          // The intro doesn't need the session; it's created while the member reads (VEX-8078).
          setFlowState("ready");

          addAPIBreadcrumb("POST /v1/verifications/legal-ids", "pending", { flow: "legal_id_verification" });

          const session = await createVerificationSession({
            sessionToken: token,
            flow: "legal_id_verification",
            externalReferenceId: resolvedReferenceId,
          });

          logger.log("[LegalIdVerification] Session created:", {
            verificationId: session.verificationId,
            hasVeriffUrl: !!session.vendorToken,
          });

          setVerificationId(session.verificationId);

          if (!session.vendorToken) {
            throw new Error("Invalid Veriff session URL from server");
          }

          // session.vendorToken = providerData.verification.url (full Veriff session URL)
          setVeriffSessionUrl(session.vendorToken);
        } catch (err) {
          logger.error("[LegalIdVerification] Session creation failed:", err);
          const errorMessage = err instanceof Error ? err.message : "Unknown error";
          const statusCode = err instanceof ApiError ? err.statusCode : undefined;
          const responseData = err instanceof ApiError ? err.responseData : undefined;

          trackFlowError(err, "session_creation_failed", "legal_id_verification", {
            endpoint: "POST /v1/verifications/legal-ids",
            failedApi: "post_legal_id_failed",
            sessionTokenPresent: !!token,
            httpStatus: statusCode,
            responseData: responseData ?? null,
          });

          const errorCode = errorCodeFromStatus(statusCode);
          trackTerminalFailure("initializing", errorCode);
          setFlowState("error");
          setError(errorMessage);
          logger.error(`[LegalIdVerification] Error [${errorCode}]${statusCode ? ` (HTTP ${statusCode})` : ""}:`, errorMessage);

          // Use `origin` directly — sendErrorToParent captures a stale parentOrigin (null)
          // at the time this listener was registered, before the state update resolved.
          // Re-validate origin is still an allowed domain before posting.
          const allowedOrigin = getAllowedDomains().some(domain => {
            const hostname = new URL(origin).hostname;
            return hostname === domain || hostname.endsWith(`.${domain}`);
          });
          if (allowedOrigin) {
            window.parent.postMessage({
              source: "idme-verification",
              type: "VERIFICATION_ERROR",
              payload: { errorCode, message: errorMessage, statusCode, failedApi: "post_legal_id_failed" as const, data: responseData },
            } as VerificationErrorMessage, origin);
          }
        }
      };

      window.addEventListener("message", handleInitMessage);

      // Signal to parent that we're ready to receive the token
      window.parent.postMessage({
        source: "idme-verification",
        type: "IDME_READY",
      }, origin);

      return () => {
        window.removeEventListener("message", handleInitMessage);
      };
    } catch (err) {
      logger.error("[LegalIdVerification] Initialization failed:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error";

      trackFlowError(err, "initialization_failed", "legal_id_verification", {
        endpoint: "POST /v1/verifications/legal-ids",
        failedApi: "post_legal_id_failed",
        sessionTokenPresent: !!sessionToken,
      });

      // Sentry-only, deliberately not routed through handleVerificationError: no
      // handshake happened, so there is no identity to bind an Outcome to. Mirrors
      // useSsnCardVerification.ts's parent-origin failure path.
      setFlowState("error");
      setError(errorMessage);
      return () => {};
    }
  };

  const sendSuccessToParent = useCallback((payload: DocumentSuccessPayload) => {
    if (!parentOrigin) {
      logger.error("[LegalIdVerification] Cannot send message - parent origin not set");
      return;
    }

    const message: VerificationCompleteMessage = {
      source: "idme-verification",
      type: "VERIFICATION_COMPLETE",
      payload,
    };
    window.parent.postMessage(message, parentOrigin);
  }, [parentOrigin]);

  const sendErrorToParent = useCallback((payload: DocumentErrorPayload) => {
    if (!parentOrigin) {
      logger.error("[LegalIdVerification] Cannot send error - parent origin not set");
      return;
    }

    const message: VerificationErrorMessage = {
      source: "idme-verification",
      type: "VERIFICATION_ERROR",
      payload,
    };
    window.parent.postMessage(message, parentOrigin);
  }, [parentOrigin]);

  // Ask the parent SDK to reload the host (top-level) page — the only reliable way
  // to re-prompt camera on iOS Safari, which this nested iframe can't do itself.
  // Triggered from Veriff's own camera-denied "Reload" screen (via onReload).
  const requestHostReload = useCallback(() => {
    if (!parentOrigin) {
      logger.error("[LegalIdVerification] Cannot request host reload - parent origin not set");
      return;
    }

    const message: VerificationReloadMessage = {
      source: "idme-verification",
      type: "VERIFICATION_RELOAD",
    };
    window.parent.postMessage(message, parentOrigin);
  }, [parentOrigin]);

  const handleVerificationError = useCallback((
    errorCode: DocumentErrorPayload["errorCode"],
    message: string,
    statusCode?: number,
    responseData?: unknown,
    failedApi?: DocumentFailedApi,
  ) => {
    // Must run before setFlowState below: the stage the flow was in is what routes
    // the Outcome, and moving to "error" is also what stops a second failure from
    // emitting a second one.
    trackTerminalFailure(stateRef.current, errorCode);
    setFlowState("error");
    setError(message);
    logger.error(`[LegalIdVerification] Error [${errorCode}]${statusCode ? ` (HTTP ${statusCode})` : ""}:`, message);

    sendErrorToParent({
      errorCode,
      message,
      statusCode,
      failedApi,
      data: responseData,
    });
  }, [sendErrorToParent]);

  const pollVerificationStatus = useCallback(async () => {
    if (!verificationId || !sessionToken) {
      handleVerificationError("network_error", "Missing verification ID or session token");
      return;
    }

    setFlowState("processing");

    try {
      logger.log("[LegalIdVerification] Polling verification status...");

      addAPIBreadcrumb(`GET /v1/verifications/legal-ids/${verificationId}`, "polling_start", {});

      // Exponential backoff polling — 60s timeout
      // Intervals: 2s → 3s → 4.5s → 6.75s → 10s → 15s → 15s... (capped at 15s)
      const initialInterval = 2000;
      const multiplier = 1.5;
      const maxInterval = 15000;
      const timeoutMs = 60000;
      const startTime = Date.now();
      let attempt = 0;
      let lastStatusCode: number | undefined = undefined;

      while (true) {
        attempt++;

        // Guard: abort if the token won't last another 2 minutes
        if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
          logger.warn("[LegalIdVerification] Token expiring soon, aborting poll");
          handleVerificationError("token_expired", "Session token has expired", undefined, undefined, "get_legal_id_failed");
          return;
        }

        try {
          const { data: result, statusCode } = await getLegalIdVerificationStatus(
            verificationId,
            sessionToken
          );
          lastStatusCode = statusCode;

          logger.log("[LegalIdVerification] Poll attempt", attempt, "status:", result?.status, "outcome:", result?.outcome);

          addAPIBreadcrumb(`GET /v1/verifications/legal-ids/${verificationId}`, statusCode, {
            attempt,
            elapsed: Date.now() - startTime,
            status: result?.status,
            outcome: result?.outcome,
          });

          // Document rejected by provider — treat as verification error, not success
          if (result?.outcome === "failed" || result?.outcome === "fraudulent") {
            logger.log("[LegalIdVerification] Document rejected, outcome:", result.outcome);
            handleVerificationError("doc_rejected", "Document verification was rejected", statusCode, result, "get_legal_id_failed");
            return;
          }

          // Verification passed — show confirmation screen before sending to parent
          if (result?.outcome === "passed") {
            setVerificationResult({ data: result });
            setFlowState("confirming");
            return;
          }

          // Check elapsed time before waiting for next attempt
          const elapsed = Date.now() - startTime;
          if (elapsed >= timeoutMs) {
            logger.log("[LegalIdVerification] Polling timeout reached after", elapsed, "ms");
            handleVerificationError("network_error", "Verification is still processing. Please try again later.", lastStatusCode, undefined, "get_legal_id_failed");
            return;
          }

          // Exponential backoff: 2s → 3s → 4.5s → ... capped at 15s
          const interval = Math.min(initialInterval * Math.pow(multiplier, attempt - 1), maxInterval);
          logger.log("[LegalIdVerification] Waiting", interval, "ms before next poll (attempt", attempt, ")");
          await new Promise(resolve => setTimeout(resolve, interval));

        } catch (pollError) {
          const statusCode = pollError instanceof ApiError ? pollError.statusCode : undefined;
          logger.warn("[LegalIdVerification] Poll attempt", attempt, "failed:", statusCode ? `HTTP ${statusCode}` : "network error", pollError);

          // 401 and 400 — fail immediately, don't retry
          if (statusCode === 401 || statusCode === 400) {
            throw pollError;
          }

          const elapsed = Date.now() - startTime;
          if (elapsed >= timeoutMs) {
            logger.log("[LegalIdVerification] Polling timeout reached after error, elapsed:", elapsed, "ms");
            throw Object.assign(new Error("Network error: unable to retrieve verification status"), { statusCode });
          }

          // Exponential backoff before retry (5xx and network errors are retried)
          const interval = Math.min(initialInterval * Math.pow(multiplier, attempt - 1), maxInterval);
          await new Promise(resolve => setTimeout(resolve, interval));
        }
      }
    } catch (err) {
      logger.error("[LegalIdVerification] Failed to get verification status:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to verify status";
      const statusCode = err instanceof ApiError ? err.statusCode : (err as any)?.statusCode as number | undefined;
      const responseData = err instanceof ApiError ? err.responseData : undefined;

      handleVerificationError(errorCodeFromStatus(statusCode), errorMessage, statusCode, responseData, "get_legal_id_failed");
    }
  }, [verificationId, sessionToken, sendSuccessToParent, sendErrorToParent, handleVerificationError]);

  const confirmVerification = useCallback(async () => {
    if (!verificationId || !sessionToken || !verificationResult) {
      handleVerificationError("network_error", "Missing verification data for confirmation");
      return;
    }

    // Guard: abort if the token won't last another 2 minutes
    if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
      logger.warn("[LegalIdVerification] Token expiring soon, aborting confirmation");
      handleVerificationError("token_expired", "Session token has expired", undefined, undefined, "patch_legal_id_failed");
      return;
    }

    try {
      logger.log("[LegalIdVerification] User confirmed — calling PATCH");
      await patchLegalIdVerification(verificationId, sessionToken);
      // The attempt's single "passed" — the actual terminal success point for Legal
      // ID (unlike SSN Card, where the backend merely responding is terminal).
      trackOutcomePassed(DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION);
      setFlowState("completed");
      sendSuccessToParent(verificationResult);
    } catch (err) {
      logger.error("[LegalIdVerification] PATCH confirmation failed:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to confirm verification";
      const statusCode = err instanceof ApiError ? err.statusCode : undefined;
      const responseData = err instanceof ApiError ? err.responseData : undefined;

      trackFlowError(err, "confirmation_failed", "legal_id_verification", {
        endpoint: `PATCH /v1/verifications/legal-ids/${verificationId}`,
        failedApi: "patch_legal_id_failed",
        verificationId: verificationId || "unknown",
        sessionTokenPresent: !!sessionToken,
        httpStatus: statusCode,
        responseData: responseData ?? null,
      });

      handleVerificationError(errorCodeFromStatus(statusCode), errorMessage, statusCode, undefined, "patch_legal_id_failed");
    }
  }, [verificationId, sessionToken, verificationResult, sendSuccessToParent, handleVerificationError]);

  return {
    // State
    state,
    error,
    verificationId,
    sessionToken,
    veriffSessionUrl,
    verificationResult,
    fallbackAvailable,

    // State setters
    setState: setFlowState,

    // Methods
    initializeLegalIdVerification,
    pollVerificationStatus,
    confirmVerification,
    handleVerificationError,
    requestHostReload,
  };
}
